import { z } from 'zod';
// =========================================================
// Pedido de ensamblado (Assembly Order) en Business Central.
//
// Estructura derivada de BC Page 900 "Assembly Order Header" + Page 901
// "Assembly Order Line", expuestas como OData V4 con nombres de servicio
// AssemblyOrder + AssemblyOrderLine.
//
// El N° del pedido lo asigna BC (No. Series PENS####). Las líneas se
// auto-generan en BC a partir de la BOM del Item cuando se setea
// No_Producto + Cantidad — nuestra integración SOLO envía el header y
// deja que BC arme las líneas.
//
// El almacén destino (Location Code en el header / sección Registro)
// es la obra donde se va a consumir. Las líneas quedan siempre en
// ALM-GRAL (los materiales salen del almacén general).
// =========================================================
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Request del backend nuestro → endpoint POST /api/coladas/{id}/crear-pedido-bc.
 * El cliente (UI) envía estos campos mínimos; el dominio resuelve el resto
 * (código M10, recurso EQ) leyendo la receta_bc asociada a la colada.
 */
export const CrearPedidoEnsambladoBCRequestSchema = z.object({
    /** Almacén destino del consumo (ej: VN-L.32). Va al header del pedido. */
    codigo_almacen_destino: z.string().trim().min(1).max(20),
    /** Fecha de registro (YYYY-MM-DD). Default: hoy. */
    fecha_registro: z.string().regex(ISO_DATE).optional(),
    /** Overrride del código de producto BC (M10-xxxx). Si no se envía, se
     * toma de la receta_bc asociada a la colada. */
    codigo_producto_bc: z.string().trim().max(20).optional(),
    /** Override de cantidad (m³). Si no se envía, se toma de
     * coladas.cantidad_total_m3. */
    cantidad_m3: z.number().positive().max(10000).optional(),
    /** Notas internas que viajan al campo "External Document No." de BC.
     * Útil para trazabilidad (ej: "Colada #1234"). */
    numero_documento_externo: z.string().trim().max(50).optional(),
});
/**
 * Response del endpoint cuando BC creó el pedido OK.
 * `numero_pedido_bc` es el `No.` que asignó la No. Series de BC (PENS####).
 */
export const CrearPedidoEnsambladoBCResponseSchema = z.object({
    numero_pedido_bc: z.string(),
    /** ID interno del documento en BC (GUID o entero según versión). Lo
     * devolvemos por si en el futuro queremos linkear a la página de BC. */
    id_bc: z.string().nullable(),
    /** N° del item que terminó en el header (snapshot). */
    codigo_producto_bc: z.string(),
    /** Cantidad m³ enviada. */
    cantidad: z.number(),
    /** Almacén destino enviado. */
    codigo_almacen_destino: z.string(),
    /** URL deep-link a la página del pedido en BC (si la podemos armar). */
    url_bc: z.string().nullable(),
});
// =========================================================
// Vista previa (preview) para el modal de la UI: la app calcula los
// datos que va a enviar y los muestra para que el operador confirme
// antes del POST contra BC.
// =========================================================
// =========================================================
// Autorización previa al envío a BC.
//
// "Enviar a BC" ya no llama a BC directo: crea una SOLICITUD pendiente.
// Un Ingeniero de Proyecto (rol Ingenieria/Admin) la aprueba (dispara el
// envío real a BC en ese momento) o la rechaza con motivo (el operador
// corrige la obra y vuelve a solicitar). Ver `hor.pedidos_bc_solicitudes`
// (migración 0042) y `pedido-ensamblado-bc-dominio.ts`.
// =========================================================
export const EstadoSolicitudPedidoBCSchema = z.enum(['pendiente', 'aprobada', 'rechazada']);
export const SolicitudPedidoBCSchema = z.object({
    id: z.number().int().positive(),
    id_colada: z.number().int().positive(),
    codigo_interno_colada: z.string(),
    obra_works_no: z.string().nullable(),
    obra_display_name: z.string().nullable(),
    cantidad_m3: z.number(),
    codigo_almacen_destino: z.string(),
    estado: EstadoSolicitudPedidoBCSchema,
    solicitado_por_email: z.string().nullable(),
    fecha_solicitud: z.string(),
    resuelto_por_email: z.string().nullable(),
    fecha_resolucion: z.string().nullable(),
    motivo_rechazo: z.string().nullable(),
    /** Contexto de la colada para decidir la autorización. */
    observaciones: z.string().nullable(),
    actividad_nombre: z.string().nullable(),
    destino_raw: z.string().nullable(),
    receta_blend_nombre: z.string().nullable(),
    origen_registro: z.string().nullable(),
    /** Presentes solo cuando `estado === 'aprobada'` (BC ya la creó). */
    numero_pedido_bc: z.string().nullable(),
    url_bc: z.string().nullable(),
});
export const ListarSolicitudesPedidoBCRequestSchema = z.object({
    estado: EstadoSolicitudPedidoBCSchema.optional(),
});
export const ListaSolicitudesPedidoBCResponseSchema = z.object({
    solicitudes: z.array(SolicitudPedidoBCSchema),
});
export const RechazarSolicitudPedidoBCRequestSchema = z.object({
    motivo: z.string().trim().min(1).max(500),
});
export const PreviewPedidoEnsambladoBCSchema = z.object({
    /** Colada origen. */
    id_colada: z.number().int().positive(),
    codigo_interno_colada: z.string(),
    fecha_colada: z.string(),
    /** Item resuelto desde la receta_bc de la colada. */
    codigo_producto_bc: z.string().nullable(),
    descripcion_producto_bc: z.string().nullable(),
    /** Recurso resuelto desde la receta_bc (ej: EQ-0039 BIANCA). Solo
     * informativo — BC arma la línea de recurso desde la BOM. */
    codigo_recurso_bc: z.string().nullable(),
    recurso_bc_descripcion: z.string().nullable(),
    /** Cantidad m³ total de la colada. */
    cantidad_m3: z.number(),
    /** Obra asignada a la colada (si la hay). */
    obra_works_no: z.string().nullable(),
    obra_display_name: z.string().nullable(),
    /** N° de pedido BC ya asignado (si existe). Si !== null, el endpoint
     * de creación falla con 409 — hay que limpiar antes. */
    numero_pedido_bc_existente: z.string().nullable(),
    /** Razones por las que NO se puede crear el pedido (validación
     * preventiva en frontend). Ej: "Colada sin receta BC mapeada",
     * "Colada sin obra asignada", "Cantidad total = 0". */
    bloqueantes: z.array(z.string()),
});
//# sourceMappingURL=pedido-ensamblado-bc.js.map